import * as Sentry from '@sentry/react'

/**
 * Initialize Sentry for error tracking and performance monitoring
 * 
 * To complete setup:
 * 1. Create an account at https://sentry.io
 * 2. Create a new project (choose React)
 * 3. Copy your DSN from the project settings
 * 4. Add VITE_SENTRY_DSN to your .env file
 */

export function initSentry() {
  // Only initialize in production
  if (import.meta.env.PROD && import.meta.env.VITE_SENTRY_DSN) {
    Sentry.init({
      dsn: import.meta.env.VITE_SENTRY_DSN,
      
      // Performance Monitoring
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration(),
      ],
      
      // Performance Monitoring - capture 10% of transactions
      tracesSampleRate: 0.1,
      
      // Session Replay - capture 10% of errors
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
      
      // Send default PII (IP addresses, etc)
      sendDefaultPii: true,
      
      // Release Tracking
      release: import.meta.env.VITE_APP_VERSION || 'unknown',
      
      // Environment
      environment: import.meta.env.MODE,
      
      // User Privacy
      beforeSend(event, hint) {
        // Filter out sensitive data
        if (event.user) {
          // Only keep user ID, remove email/name for privacy
          event.user = {
            id: event.user.id
          }
        }
        
        // Filter out non-app errors (browser extensions, etc)
        if (event.exception) {
          const error = hint.originalException as Error
          // Skip errors from browser extensions
          if (error && error.stack && error.stack.includes('chrome-extension://')) {
            return null
          }
        }
        
        return event
      },
      
      // Ignore certain errors
      ignoreErrors: [
        // Browser errors
        'ResizeObserver loop limit exceeded',
        'ResizeObserver loop completed with undelivered notifications',
        // Firebase errors that are user-facing
        'permission-denied',
        'Missing or insufficient permissions',
        // Network errors
        'NetworkError',
        'Failed to fetch',
        // Mapbox warnings
        'Invalid LngLat',
      ],
    })
  }
}

// Helper to add user context
export function setSentryUser(user: { id: string; email?: string }) {
  if (import.meta.env.PROD) {
    Sentry.setUser({
      id: user.id,
      // Don't send email for privacy
    })
  }
}

// Helper to clear user context on logout
export function clearSentryUser() {
  if (import.meta.env.PROD) {
    Sentry.setUser(null)
  }
}

// Helper to capture custom errors with context
export function captureError(error: Error, context?: Record<string, any>) {
  if (import.meta.env.PROD) {
    Sentry.captureException(error, {
      contexts: {
        custom: context
      }
    })
  } else {
    // In development, just log to console
    console.error('Error:', error, context)
  }
}

// Helper to track custom events
export function trackEvent(eventName: string, data?: Record<string, any>) {
  if (import.meta.env.PROD) {
    Sentry.captureMessage(eventName, {
      level: 'info',
      extra: data
    })
  }
}